import { LoaderFunctionArgs, redirect } from 'react-router-dom';
import { AuthLoaderFunction, LoaderUserData } from '../../types/auth';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { Guard } from './types';
import { assertGuardError, assertMissingDataError } from './helpers';
import { setUserData } from './setters';

interface Options {
  loader?: AuthLoaderFunction;
  setters?: ((args: LoaderFunctionArgs) => Promise<void>)[];
  provisionalSetters?: ((args: LoaderFunctionArgs) => Promise<void>)[];
}

export function parallel(
  guards: Guard[],
  { loader, setters, provisionalSetters }: Options = {
    loader: undefined,
    setters: undefined,
    provisionalSetters: undefined,
  },
) {
  return async (args: LoaderFunctionArgs) => {
    const parsedSetters = setters ? [setUserData, ...setters] : [setUserData];
    try {
      const settersPromises = parsedSetters?.map((setter) => setter(args));
      await Promise.all(settersPromises || []);
      const promises = guards.map((guard) => guard(args));
      await Promise.all(promises);

      const user = await getUserData();

      return loader?.(args, user) || null;
    } catch (e) {
      console.error(e);
      if (assertGuardError(e)) {
        const reqUrl = new URL(args.request.url);
        const redirectUrl = new URL(e.redirectUrl, window.location.origin);
        if (reqUrl.searchParams.get('redirectUrl')) {
          redirectUrl.searchParams.set(
            'redirectUrl',
            reqUrl.searchParams.get('redirectUrl') as string,
          );
        }
        return redirect(redirectUrl.toString());
      } else if (assertMissingDataError(e) && provisionalSetters) {
        const user = await getUserData();
        const provisionalSettersPromises = provisionalSetters.map((setter) =>
          setter(args),
        );
        await Promise.all(provisionalSettersPromises);
        return loader?.(args, user) || null;
      } else {
        throw e;
      }
    }
  };
}

async function getUserData() {
  const workspacePermission = useUserPermissionsStore.getState().workspacePermission;
  const organizationPermission =
    useUserPermissionsStore.getState().organizationPermission;
  const userId = useUserPermissionsStore.getState().userId;
  const email = useUserPermissionsStore.getState().email;
  const isEmailVerified = useUserPermissionsStore.getState().isEmailVerified;
  return {
    workspacePermission,
    organizationPermission,
    userId,
    email,
    isEmailVerified,
  } as LoaderUserData;
}
